//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import {
  DropdownMenu as DropdownMenuRoot,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import switcherIcon from '../../icons/switcher-grid.svg';
import { navBarStyle } from '../../styles/navBarStyle';
import SolutionsDropdown from '../navBar/DropdownMenu';
import { useTeamspaceContext } from '../../TeamspaceContext';
import CustomSnackbar from '../common/CustomSnackbar';

interface IframeContentProps {
  hasNexusRole: boolean | undefined;
  t: (key: string) => string;
}

const Switcher: React.FC<IframeContentProps> = ({ hasNexusRole, t }) => {
  const { teamspaceName } = useTeamspaceContext();
  const isDisabled = !hasNexusRole || teamspaceName === 'Initial TeamSpace';
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleDisabledClick = () => {
    if (isDisabled) {
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      <DropdownMenuRoot
        open={open}
        onOpenChange={(nextOpen) => {
          if (!isDisabled) {
            setOpen(nextOpen);
          }
        }}
      >
        <div
          className={`flex items-center justify-center p-2 ${isDisabled ? 'cursor-default' : 'cursor-pointer'}`}
          id="switcher-container"
          data-testid="switcher"
          onClick={handleDisabledClick}
          onKeyDown={(e) => {
            if (isDisabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleDisabledClick();
            }
          }}
          role={isDisabled ? 'button' : undefined}
          tabIndex={isDisabled ? 0 : undefined}
        >
          <DropdownMenuTrigger asChild>
            <button
              disabled={isDisabled}
              aria-label={t('logoAlt')}
              type="button"
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1"
              style={isDisabled ? { pointerEvents: 'none' } : undefined}
            >
              <img
                src={switcherIcon}
                style={
                  open
                    ? { ...navBarStyle.logoHighlighted }
                    : { ...navBarStyle.logo }
                }
                className={!hasNexusRole ? 'disabled' : ''}
                alt={t('logoAlt')}
                id="switcher-icon"
              />
            </button>
          </DropdownMenuTrigger>
          {!isDisabled && (
            <DropdownMenuContent
              className="mt-1 w-64 p-4 dark:border-gray-700 dark:bg-[#262626]"
              data-testid="dropdown-menu"
              id="dropdown-menu"
              align="end"
            >
              <SolutionsDropdown />
            </DropdownMenuContent>
          )}
        </div>
      </DropdownMenuRoot>
      <CustomSnackbar
        open={snackbarOpen}
        message={t('switcher-teamspace-required')}
        severity="info"
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default Switcher;
